import { createContext, useContext } from 'react';
import { useAuth } from './auth';

type AccessContextType = {
  isGranted: (attribute: string, subject?: any) => boolean;
  isNotGranted: (attribute: string, subject?: any) => boolean;
  isAnyGranted: (attributes: string[], subject?: any) => boolean;
  isAdmin: () => boolean;
  isScheduler: () => boolean;
  isDealerGroupManager: () => boolean;
  isDealerRegionalManager: () => boolean;
  isDealerStoreManager: () => boolean;
  isDealerFinanceManager: () => boolean;
  isDealerSalesManager: () => boolean;
  isDealerUpperManager: () => boolean;
  isDealerLowerManager: () => boolean;
  isDealerManager: () => boolean;
  isDealerStoreUser: () => boolean;
  isAdminType: () => boolean;
  isDealerType: () => boolean;
};

const AccessContext = createContext<AccessContextType | null>(null);

type AccessProviderProps = {
  children: React.ReactNode;
};

const AccessProvider = ({ children }: AccessProviderProps) => {
  const { user: authUser } = useAuth();

  const isAdmin = () => authUser && authUser.isAdmin();
  const isScheduler = () => authUser && authUser.isScheduler();
  const isDealerGroupManager = () => authUser && authUser.isDealerGroupManager();
  const isDealerRegionalManager = () => authUser && authUser.isDealerRegionalManager();
  const isDealerStoreManager = () => authUser && authUser.isDealerStoreManager();
  const isDealerFinanceManager = () => authUser && authUser.isDealerFinanceManager();
  const isDealerSalesManager = () => authUser && authUser.isDealerSalesManager();
  const isDealerUpperManager = () => authUser && authUser.isDealerUpperManager();
  const isDealerLowerManager = () => authUser && authUser.isDealerLowerManager();
  const isDealerManager = () => authUser && authUser.isDealerManager();
  const isDealerStoreUser = () => authUser && authUser.isDealerStoreUser();

  const isAdminType = () => authUser && authUser.isAdminType();
  const isDealerType = () => authUser && authUser.isDealerType();

  const isGranted = (attribute: string, subject?: any) => {
    switch (attribute) {
      default:
        return false;
    }
  }

  const isNotGranted = (attribute: string, subject?: any) => !isGranted(attribute, subject);

  const isAnyGranted = (attributes: string[], subject?: any) => attributes.some(attr => isGranted(attr, subject));

  const contextValue = {
    isGranted,
    isNotGranted,
    isAnyGranted,
    isAdmin,
    isScheduler,
    isDealerGroupManager,
    isDealerRegionalManager,
    isDealerStoreManager,
    isDealerFinanceManager,
    isDealerSalesManager,
    isDealerUpperManager,
    isDealerLowerManager,
    isDealerManager,
    isDealerStoreUser,
    isAdminType,
    isDealerType,
  };

  return <AccessContext.Provider value={contextValue}>{children}</AccessContext.Provider>
}

// helper hook that makes context data available
export const useAccess = () => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error('useAccess must be used within AccessProvider');
  }
  return context;
};

export default AccessProvider;